import type { Microsoft365TurnCode } from '@dbrain/shared';
import { Button, Icons, type IconComponent } from '@dbrain/ui';
import { useEffect, useId, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMicrosoft365Status } from '../../api/microsoft365.js';
import { Microsoft365ConnectDialog } from './ConnectDialog.js';
import { ConsentRequestButton } from './parts.js';
import { noticeView, upgradeFeatures, type NoticeIcon } from './state.js';

const ICONS: Record<NoticeIcon, IconComponent> = {
  mail: Icons.mail,
  regenerate: Icons.regenerate,
  shield: Icons.shield,
  info: Icons.info,
  lock: Icons.lock,
  done: Icons.done,
};

/**
 * A turn that stopped for Microsoft 365 (MsDurumlar; `turn.completed.errorCode` in MICROSOFT365_TURN_CODES):
 * "Bunun için Microsoft hesabınızı bağlayın", "Microsoft bağlantınızı yenilemeniz gerekiyor", "Şirketinizin onayı
 * bekleniyor", "Microsoft 365 şu an kullanılamıyor" or why it is off for the person. Only the conversation's
 * latest notice has a button: "Hesabımı bağla" / "Yeniden bağlan" open the connect dialog, which comes back to
 * this conversation (`?microsoft365=resume`, the page then continues the question once); a code sign-in that
 * finishes here calls `onConnected` at once. Connected by now (another tab): "Devam et" answers the question;
 * Microsoft unreachable: "Yeniden dene" asks it again the same way (v0.15e.1, no mockup). Part 3 (no mockup): "Yeni
 * izin gerekiyor" opens the upgrade sign-in for the permissions still missing, and the question goes on after it.
 */
export function Microsoft365Notice({
  code,
  conversationId,
  latest,
  onConnected,
}: {
  code: Microsoft365TurnCode;
  conversationId: string;
  /** the conversation's last item and no turn running: only then the buttons */
  latest: boolean;
  /** continue the conversation (POST /api/conversations/:id/continue), once */
  onConnected: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const status = useMicrosoft365Status().data;
  const [connecting, setConnecting] = useState(false);
  const [continued, setContinued] = useState(false);
  // a continue that started a turn takes this notice's buttons away; one that did not frees the button again
  useEffect(() => {
    if (!continued) return;
    const timer = setTimeout(() => setContinued(false), 10_000);
    return () => clearTimeout(timer);
  }, [continued]);
  const view = noticeView(code, status, latest);
  const Icon = ICONS[view.icon];
  const proceed = () => {
    setContinued(true);
    onConnected();
  };
  return (
    <section
      className={`m365-notice m365-notice--${view.tone}`}
      aria-labelledby={titleId}
      role={latest ? 'status' : undefined}
      data-testid="microsoft365-notice"
      data-code={code}
      data-view={view.key}
    >
      <span className="m365-notice__icon" aria-hidden="true">
        <Icon size={18} />
      </span>
      <div className="m365-notice__body">
        <h2 id={titleId} className="m365-notice__title">
          {t(`microsoft365.user.notice.${view.key}.title`, view.values ?? {})}
        </h2>
        <p className="m365-notice__text">
          {view.key === 'unavailableAdmin' ? (
            <Trans i18nKey="microsoft365.user.notice.unavailableAdmin.text" components={{ go: <Link to="/admin/integrations" data-testid="microsoft365-notice-admin" /> }} />
          ) : (
            t(`microsoft365.user.notice.${view.key}.text`, view.values ?? {})
          )}
        </p>
        <div className="m365-notice__actions">
          {view.action === 'connect' || view.action === 'reconnect' ? (
            <Button
              size="sm"
              icon={view.action === 'reconnect' ? <Icons.regenerate size={15} /> : undefined}
              onClick={() => setConnecting(true)}
              data-testid={view.action === 'reconnect' ? 'microsoft365-notice-reconnect' : 'microsoft365-notice-connect'}
            >
              {view.action === 'reconnect' ? t('microsoft365.user.card.reconnect') : t('microsoft365.user.card.connect')}
            </Button>
          ) : view.action === 'consent' ? (
            <ConsentRequestButton />
          ) : view.action === 'continue' ? (
            <Button size="sm" busy={continued} onClick={proceed} data-testid="microsoft365-notice-continue">
              {t('microsoft365.user.notice.continue')}
            </Button>
          ) : view.action === 'retry' ? (
            <Button size="sm" icon={<Icons.regenerate size={15} />} busy={continued} onClick={proceed} data-testid="microsoft365-notice-retry">
              {t('microsoft365.user.card.retry')}
            </Button>
          ) : view.action === 'connectors' ? (
            <Link className="ui-button ui-button--secondary ui-button--sm" to="/connectors" data-testid="microsoft365-notice-connectors">
              {t('microsoft365.user.notice.connectors')}
            </Link>
          ) : null}
          <details className="m365-notice__details">
            <summary>{t('common.technicalDetails')}</summary>
            <p>
              {t('common.errorCode')}: {code}
            </p>
          </details>
        </div>
      </div>
      {connecting ? (
        <Microsoft365ConnectDialog
          returnTo={`/c/${conversationId}?microsoft365=resume`}
          // part 3: "Yeni izin gerekiyor" asks Microsoft for the permissions still missing (features, never scopes)
          {...(view.key === 'scopes' ? { features: upgradeFeatures(status) } : {})}
          onClose={() => setConnecting(false)}
          onConnected={() => {
            setContinued(true);
            onConnected();
          }}
        />
      ) : null}
    </section>
  );
}
